import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { completeProfileRequestSchema, type CompleteProfileRequest } from '@vagusx/clients';
import { useQueryClient } from '@tanstack/react-query';
import { API_URL } from '@/lib/env';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type CompleteProfileForm = CompleteProfileRequest;

export default function CompleteProfile(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompleteProfileForm>({
    resolver: zodResolver(completeProfileRequestSchema),
  });

  const onSubmit = async (data: CompleteProfileForm): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ practiceName: data.practiceName }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to complete setup');
        return;
      }

      // Invalidate all queries to refetch with updated session
      await queryClient.invalidateQueries();
      navigate({ to: '/' });
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm border-0 shadow-lg">
        <CardHeader>
          <p className="text-sm font-semibold tracking-wide text-muted-foreground">VagusX</p>
          <CardTitle className="text-2xl font-bold">Set up your practice</CardTitle>
          <CardDescription>Enter your practice name to complete setup</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="practiceName">Practice Name</Label>
              <Input
                id="practiceName"
                type="text"
                placeholder="Acme Health Clinic"
                {...register('practiceName')}
                disabled={isLoading}
              />
              {errors.practiceName && (
                <p className="text-sm text-destructive">{errors.practiceName.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Setting up...' : 'Continue'}
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
